// ─────────────────────────────────────────────────────────────────────────────
// QUIZ CONTENT & SCORING
// Edit this file to update quiz questions, options, scores, and scoring logic.
// Changes here automatically apply to both the web app (App.js) and PDF report.
// ─────────────────────────────────────────────────────────────────────────────

// Jurisdictional advisories and mandates — keyed by question option value
export const JURISDICTIONS = {
  us: {
    name: "United States",
    authority: "NSA / NIST",
    instrument: "CNSA 2.0 + NIST FIPS 203/204/205",
    note: "NIST FIPS 203, 204 and 205 were finalised in August 2024. NSA CNSA 2.0 sets NSS transition by 2035, with a first procurement cutoff on January 1, 2027. NSM-10 sets the federal policy ceiling at 2035.",
  },
  eu: {
    name: "European Union",
    authority: "EC / ENISA",
    instrument: "Coordinated PQC Roadmap (June 2025) + DORA",
    note: "The EU Coordinated Roadmap (June 2025) calls for national strategies by end-2026 and quantum-safe treatment of high-risk systems by end-2030. DORA reinforces cryptographic resilience expectations for regulated entities.",
  },
  uk: {
    name: "United Kingdom",
    authority: "NCSC",
    instrument: "Timeline for Migration to PQC (March 2025)",
    note: "The NCSC roadmap sets discovery and planning by 2028, high-priority migration by 2031, and full transition by 2035.",
  },
  ca: {
    name: "Canada",
    authority: "CCCS / CSE",
    instrument: "Government PQC Roadmap (June 2025)",
    note: "Canada's government roadmap calls for initial departmental plans by April 2026, high-risk systems transitioned by end-2031, and full transition by 2035.",
  },
  au: {
    name: "Australia",
    authority: "ASD / ACSC",
    instrument: "ASD PQC transition guidance",
    note: "ASD guidance indicates traditional asymmetric cryptography should not be relied on beyond 2030, with transition plans expected by end-2026.",
  },
  sg: {
    name: "Singapore",
    authority: "CSA / MAS",
    instrument: "MAS Circular MAS/TCRS/2024/01 + CSA guidance",
    note: "MAS Circular MAS/TCRS/2024/01 reinforces financial-sector cryptographic resilience requirements. CSA quantum guidance is under development.",
  },
  jp: {
    name: "Japan",
    authority: "NISC / CRYPTREC",
    instrument: "CRYPTREC migration framework",
    note: "Japan's CRYPTREC framework supports national government coordination on migration and is aligned with NIST-standardised PQC algorithms.",
  },
  kr: {
    name: "South Korea",
    authority: "NIS / KISA",
    instrument: "KISA-coordinated PQC framework",
    note: "South Korea is progressing a KISA-coordinated framework and national PQC research programme, aligned with NIST standards.",
  },
  g7: {
    name: "G7",
    authority: "G7 Cyber Expert Group (co-chaired by US Treasury & Bank of England)",
    instrument: "Coordinated Roadmap for the Transition to PQC in the Financial Sector (Jan 2026)",
    note: "Non-binding G7 roadmap aligning member economies on a phased migration — inventory, risk assessment, deployment and monitoring — with a mid-2030s planning horizon and earlier migration for critical financial systems. Treats quantum-safe migration as a core operational-resilience issue.",
  },
};

// Jurisdiction keys whose selection should also surface the G7-CEG advisory
export const G7_JURISDICTIONS = ["us", "eu", "uk", "ca", "jp"];

export const PROFILE_QUESTIONS = [
  {
    id: "industry",
    label: "What is your industry?",
    type: "single",
    options: [
      { value: "finserv",    label: "Financial Services" },
      { value: "healthcare", label: "Healthcare & Life Sciences" },
      { value: "government", label: "Government & Defence" },
      { value: "tech",       label: "Technology & Software" },
      { value: "energy",     label: "Energy & Critical Infrastructure" },
      { value: "other",      label: "Other" },
    ],
  },
  {
    id: "size",
    label: "How large is your organisation?",
    type: "single",
    options: [
      { value: "small", label: "Under 500 employees" },
      { value: "mid",   label: "500 – 5,000 employees" },
      { value: "large", label: "Over 5,000 employees" },
    ],
  },
];

export const SCORED_QUESTIONS = [
  {
    id: "ecc",
    section: "Cryptographic Exposure",
    label: "Which of the following does your organisation use? (These typically rely on encryption that quantum computers can break.)",
    hint: "Select all that apply",
    type: "multi",
    options: [
      { value: "tls",     label: "Customer-facing TLS / HTTPS",          weight: 2 },
      { value: "api",     label: "API authentication",                   weight: 2 },
      { value: "signing", label: "Digital document or code signing",     weight: 3 },
      { value: "auth",    label: "Internal user authentication",         weight: 2 },
      { value: "payment", label: "Payment or transaction authorisation", weight: 4 },
      { value: "none",    label: "None of the above / I'm not sure",     weight: 1, exclusive: true },
    ],
  },
  {
    id: "shelf",
    section: "Cryptographic Exposure",
    label: "How long does your most sensitive data need to remain confidential?",
    hint: "Think contracts, client records, intellectual property, or anything that would still be damaging if exposed in 5–10 years",
    type: "single",
    options: [
      { value: "short", label: "Less than 2 years",  score: 80 },
      { value: "med",   label: "2 – 5 years",         score: 50 },
      { value: "long",  label: "5 – 10 years",        score: 20 },
      { value: "vlong", label: "More than 10 years",  score: 5  },
    ],
  },
  {
    id: "signatures",
    section: "Signature Integrity",
    label: "Does your organisation use digital signatures to approve or authorise any of the following?",
    hint: "Select all that apply",
    type: "multi",
    options: [
      { value: "payments",   label: "Financial transactions or payments",        weight: 4 },
      { value: "legal",      label: "Legal documents or contracts",              weight: 3 },
      { value: "regulatory", label: "Regulatory submissions or filings",         weight: 3 },
      { value: "admin",      label: "System administration or privileged access", weight: 3 },
      { value: "none",       label: "None of the above / I'm not sure",          weight: 0, exclusive: true },
    ],
  },
  {
    id: "inventory",
    section: "Cryptographic Exposure",
    label: "Has your organisation completed a Quantum Threat Assessment (QTA) in the last 12 months?",
    hint: "A QTA identifies where quantum risk lives in your systems: across your data, systems, and cryptographic usage. Without one, your exposure is largely invisible.",
    type: "single",
    options: [
      { value: "yes",        label: "Yes — formal QTA completed",       score: 90 },
      { value: "inprogress", label: "In progress",                      score: 60 },
      { value: "partial",    label: "Partially — scoped review only",   score: 50 },
      { value: "no",         label: "No — not yet completed",           score: 15 },
      { value: "unknown",    label: "None of the above / I'm not sure", score: 5  },
    ],
  },
  {
    id: "pqc_plan",
    section: "Readiness",
    label: "Does your organisation have a post-quantum cryptography (PQC) migration plan?",
    type: "single",
    options: [
      { value: "yes",       label: "Yes — fully scoped and resourced",     score: 90 },
      { value: "discuss",   label: "Under discussion but not formalised",  score: 45 },
      { value: "no",        label: "No",                                   score: 10 },
      { value: "whatispqc", label: "No, and we're unsure where to start",   score: 0  },
    ],
  },
  {
    id: "board",
    section: "Readiness",
    label: "Has your board or senior leadership been briefed on the quantum security threat?",
    type: "single",
    options: [
      { value: "yes",        label: "Yes — formal briefing completed",                  score: 90 },
      { value: "informal",   label: "Informally — some awareness",                      score: 40 },
      { value: "no",         label: "No — not yet raised",                              score: 5  },
      { value: "considered", label: "No, and we've considered it and decided not to.",  score: 5  },
    ],
  },
  {
    id: "vendors",
    section: "Readiness",
    label: "Do your vendor contracts include any cryptographic security or PQC requirements?",
    type: "single",
    options: [
      { value: "yes",     label: "Yes — PQC clauses in contracts",    score: 90 },
      { value: "general", label: "General security requirements only", score: 40 },
      { value: "unsure",  label: "None of the above / I'm not sure",  score: 10 },
      { value: "no",      label: "No",                                score: 5  },
    ],
  },
  {
    id: "jurisdictions",
    section: "Readiness",
    label: "In which jurisdictions does your organisation operate or serve customers?",
    hint: "Select all that apply. Your report will flag the specific regulatory obligations active in each jurisdiction.",
    type: "multi",
    options: [
      { value: "us",     label: "United States",    weight: 0 },
      { value: "eu",     label: "European Union",   weight: 0 },
      { value: "uk",     label: "United Kingdom",   weight: 0 },
      { value: "ca",     label: "Canada",           weight: 0 },
      { value: "au",     label: "Australia",        weight: 0 },
      { value: "sg",     label: "Singapore",        weight: 0 },
      { value: "jp",     label: "Japan",            weight: 0 },
      { value: "kr",     label: "South Korea",      weight: 0 },
      { value: "none",   label: "None of the above / I'm not sure", weight: 0, exclusive: true },
    ],
  },
];

// Labels for the score dimension bars — used in both the web app and PDF report.
// Update these labels here to keep the web app and PDF in sync automatically.
export const SCORE_DIMENSIONS = [
  { key: "ce",   label: "Cryptographic exposure" },
  { key: "sndl", label: "Store now, decrypt later" },
  { key: "tnfl", label: "Signature integrity" },
];

// ─────────────────────────────────────────────────────────────────────────────
// SCORING
// ─────────────────────────────────────────────────────────────────────────────

export function computeScores(answers) {
  let ceRaw = 0;
  const eccAns = answers.ecc || [];
  if (!eccAns.includes("none")) {
    const eccQ = SCORED_QUESTIONS.find(q => q.id === "ecc");
    const total = eccQ.options.filter(o => !o.exclusive).reduce((s, o) => s + o.weight, 0);
    const selected = eccQ.options.filter(o => eccAns.includes(o.value)).reduce((s, o) => s + o.weight, 0);
    ceRaw += Math.round(100 - (selected / total) * 100);
  } else {
    ceRaw += 80;
  }
  const invOpt = SCORED_QUESTIONS.find(q => q.id === "inventory").options.find(o => o.value === answers.inventory);
  ceRaw += invOpt ? invOpt.score : 0;
  const ce = Math.round(ceRaw / 2);

  const shelfOpt = SCORED_QUESTIONS.find(q => q.id === "shelf").options.find(o => o.value === answers.shelf);
  const sndl = shelfOpt ? shelfOpt.score : 50;

  let tnfl = 100;
  const sigAns = answers.signatures || [];
  if (!sigAns.includes("none")) {
    const sigQ = SCORED_QUESTIONS.find(q => q.id === "signatures");
    const total = sigQ.options.filter(o => !o.exclusive).reduce((s, o) => s + o.weight, 0);
    const selected = sigQ.options.filter(o => sigAns.includes(o.value)).reduce((s, o) => s + o.weight, 0);
    tnfl = Math.round(100 - (selected / total) * 100);
  }

  const pqcOpt    = SCORED_QUESTIONS.find(q => q.id === "pqc_plan").options.find(o => o.value === answers.pqc_plan);
  const boardOpt  = SCORED_QUESTIONS.find(q => q.id === "board").options.find(o => o.value === answers.board);
  const vendorOpt = SCORED_QUESTIONS.find(q => q.id === "vendors").options.find(o => o.value === answers.vendors);
  const readiness = Math.round(((pqcOpt?.score || 0) + (boardOpt?.score || 0) + (vendorOpt?.score || 0)) / 3);

  const overall = Math.round(ce * 0.3 + sndl * 0.2 + tnfl * 0.3 + readiness * 0.2);
  return { ce, sndl, tnfl, readiness, overall };
}

export function band(score) {
  if (score >= 80) return { label: "Low risk",      shortLabel: "Low",      color: "#0F7A4A", status: "COMPLIANT TRAJECTORY" };
  if (score >= 67) return { label: "Moderate risk", shortLabel: "Moderate", color: "#EAB308", status: "MONITOR CLOSELY" };
  if (score >= 50) return { label: "Elevated risk", shortLabel: "Elevated", color: "#EAB308", status: "REMEDIATION REQUIRED" };
  if (score >= 34) return { label: "High risk",     shortLabel: "High",     color: "#C8102E", status: "URGENT ACTION NEEDED" };
  return               { label: "Critical risk", shortLabel: "Critical", color: "#C8102E", status: "IMMEDIATE ACTION" };
}

export function preparedness(readinessScore, overallScore) {
  const riskHigh = overallScore < 50;

  if (readinessScore >= 67) {
    return {
      label: "Strong",
      color: "#0F7A4A",
      bg: "#EAF3DE",
      dot: "#0F7A4A",
      desc: "Migration planning is underway, leadership is briefed, and vendor requirements are documented. You have the internal foundations to act.",
      cta: riskHigh
        ? "You have momentum — you need a structured roadmap to convert awareness into execution before the 2027 deadlines."
        : "You're ahead of most organisations. A structured QRI engagement will close remaining gaps and put you on a fully defensible compliance trajectory.",
    };
  }
  if (readinessScore >= 34) {
    return {
      label: "Developing",
      color: "#EAB308",
      bg: "#FAEEDA",
      dot: "#EAB308",
      desc: "Some awareness exists and discussions are underway, but no formal plan or dedicated resources are in place yet.",
      cta: riskHigh
        ? "You understand the urgency — you need a structured roadmap and external expertise to move from awareness to action."
        : "Your exposure is manageable but internal readiness is still developing. External support will accelerate your timeline significantly.",
    };
  }
  return {
    label: "Limited",
    color: "#C8102E",
    bg: "#FCEBEB",
    dot: "#C8102E",
    desc: "No PQC migration plan, no board briefing, and no cryptographic requirements in vendor contracts.",
    cta: riskHigh
      ? "Your organisation is significantly exposed and lacks the internal foundations to respond without external support."
      : "Your current exposure is containable — but without a response programme in place, you are falling behind the curve.",
  };
}

export function generateFindings(answers, scores) {
  const findings = [];
  const sigAns = answers.signatures || [];
  const selectedJurisdictions = (answers.jurisdictions || []).filter(j => JURISDICTIONS[j]);

  findings.push({
    kind: "info",
    text: "Start with a quantum threat assessment to pinpoint your quantum exposure and prioritize immediate next steps.",
  });

  if (sigAns.includes("payments") && scores.tnfl < 50)
    findings.push({ kind: "alert", text: "Your payment authorisation infrastructure relies on ECC digital signatures — directly vulnerable to real-time quantum forgery once a CRQC becomes available." });

  if (["long", "vlong"].includes(answers.shelf) && scores.sndl < 50)
    findings.push({ kind: "alert", text: "Your sensitive data is likely to remain valuable beyond the estimated quantum threat window, placing it at active harvest-now-decrypt-later risk today." });

  if (["no", "unknown"].includes(answers.inventory))
    findings.push({ kind: "warn", text: "Without a completed Quantum Threat Assessment, you have limited visibility into your quantum exposure and cannot reliably prioritize migration actions." });

  if (answers.pqc_plan === "whatispqc")
    findings.push({ kind: "warn", text: "Post-quantum cryptography is now a live regulatory requirement under DORA (Jan 2025) and CNSA 2.0. Lack of awareness at leadership level is itself a compliance risk." });

  if (selectedJurisdictions.length > 0) {
    const names = selectedJurisdictions.map(j => JURISDICTIONS[j].name);
    const preview = names.length <= 2 ? names.join(" and ") : `${names.slice(0, 2).join(", ")} and ${names.length - 2} more jurisdiction(s)`;
    findings.push({
      kind: "info",
      text: `Regulatory obligations are active across ${preview}. Review the jurisdiction advisory section to align migration timelines and compliance milestones.`,
    });
  }

  if (answers.vendors === "no")
    findings.push({ kind: "warn", text: "Your vendor contracts contain no cryptographic requirements. Under DORA Art. 30, quantum ICT third-party risk must be addressed in procurement." });

  if (answers.board === "no")
    findings.push({ kind: "warn", text: "Your board has not been briefed on quantum risk. Under DORA and NIS2, management body accountability for emerging cryptographic threats is a regulatory requirement." });

  return findings.slice(0, 4);
}
